import React, { useState, useEffect } from 'react';
import { Beaker, ArrowLeftRight } from 'lucide-react';
import { SEO } from '../components/SEO';

interface ConversionState {
  substance: string;
  result: number | null;
}

// Density values in g/ml for common substances
const substanceDensities: Record<string, number> = {
  'water': 1.0,
  'milk': 1.03,
  'oil': 0.92,
  'honey': 1.42,
  'flour': 0.53,
  'sugar': 0.85,
  'salt': 1.2,
  'rice': 0.78,
  'coffee': 0.56,
  'yogurt': 1.05,
  'cream': 0.98,
  'butter': 0.96,
  'vinegar': 1.01,
  'alcohol': 0.79,
  'gasoline': 0.75,
  'mercury': 13.6,
  'blood': 1.06,
  'chocolate': 1.33,
  'maple-syrup': 1.37,
  'corn-syrup': 1.38
};

const substanceNames: Record<string, string> = {
  'water': 'Voda',
  'milk': 'Mlijeko',
  'oil': 'Ulje',
  'honey': 'Med',
  'flour': 'Brašno',
  'sugar': 'Šećer',
  'salt': 'Sol',
  'rice': 'Riža',
  'coffee': 'Kava',
  'yogurt': 'Jogurt',
  'cream': 'Vrhnje',
  'butter': 'Maslac',
  'vinegar': 'Ocat',
  'alcohol': 'Alkohol',
  'gasoline': 'Benzin',
  'mercury': 'Živa',
  'blood': 'Krv',
  'chocolate': 'Čokolada',
  'maple-syrup': 'Javorov sirup',
  'corn-syrup': 'Kukuruzni sirup'
};

function GramToMl672g() {
  const [values, setValues] = useState<ConversionState>({
    substance: 'water',
    result: null
  });

  const calculateConversion = () => {
    const density = substanceDensities[values.substance];
    
    // Convert 14 grams to milliliters
    const result = 672 / density;
    
    return result;
  };

  const handleInputChange = (field: keyof ConversionState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value,
      result: null
    }));
  };

  useEffect(() => {
    const result = calculateConversion();
    setValues(prev => ({
      ...prev,
      result: parseFloat(result.toFixed(3))
    }));
  }, [values.substance]);

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('hr-HR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3
    }).format(num);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "672 Grama u ML Kalkulator",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Besplatni online kalkulator za pretvaranje 672 grama u mililitre za različite tvari. Saznajte koliko mililitara zauzimaju 672 grama različitih tvari.",
    "url": "https://online-kalkulator.com/gram-u-ml/672g-u-ml",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Pretvaranje 672 grama u mililitre",
      "Podrška za različite tvari",
      "Automatsko ažuriranje rezultata",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="672 Grama u Mililitar | Koliko su 672 Grama u mL"
        description="Saznajte koliko mililitara zauzimaju 672 grama različitih tvari. Besplatni online kalkulator za pretvaranje 672 grama u mililitre."
        canonicalUrl="/gram-u-ml/672g-u-ml"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Beaker className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">672 Grama u ML Kalkulator</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Substance Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Odaberite tvar
                </label>
                <select
                  value={values.substance}
                  onChange={(e) => handleInputChange('substance', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {Object.entries(substanceNames).map(([value, name]) => (
                    <option key={value} value={value}>{name}</option>
                  ))}
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  Gustoća: {substanceDensities[values.substance]} g/ml
                </p>
              </div>

              {/* Result Display */}
              <div className="border-t pt-4">
                <div className="text-center">
                  <div className="text-gray-600 mb-2">
                    672 grama {substanceNames[values.substance].toLowerCase()} =
                  </div>
                  <div className="text-2xl font-bold text-blue-600">
                    {values.result !== null ? formatNumber(values.result) : '-'} ml
                  </div>
                  <div className="text-sm text-gray-500 mt-2">
                    Formula: ml = g ÷ gustoća
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Pretvorbi 672 Grama u Mililitre</h2>
                <p className="text-gray-600 leading-relaxed">
                  Pretvorba 672 grama u mililitre ovisi o gustoći tvari. Za razliku od pretvorbe između jedinica iste vrste 
                  (npr. kilogram u gram), pretvorba između mase (gram) i volumena (mililitar) zahtijeva poznavanje gustoće tvari.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Radi Pretvorba</h2>
                <p className="text-gray-600 mb-4">
                  Pretvorba 672 grama u mililitre temelji se na jednostavnoj formuli:
                </p>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-gray-700 font-medium">
                    Volumen (ml) = Masa (g) ÷ Gustoća (g/ml)
                  </p>
                </div>
                <p className="text-gray-600 mt-4">
                  Za 672 grama, formula postaje još jednostavnija:
                </p>
                <div className="p-4 bg-blue-50 rounded-lg mt-2">
                  <p className="text-gray-700 font-medium">
                    Volumen (ml) = 672 ÷ Gustoća (g/ml)
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Primjeri Pretvorbe 14 Grama u Mililitre</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tvar
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Gustoća (g/ml)
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          672 grama = ? ml
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Voda</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">1.0</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">14.000 ml</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Brašno</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">0.53</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">26.415 ml</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Ulje</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">0.92</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">15.217 ml</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Med</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">1.42</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">9.859 ml</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Živa</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">13.6</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">1.029 ml</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Odaberite tvar iz padajućeg izbornika</li>
                  <li>Rezultat će se automatski izračunati</li>
                  <li>Prikazat će se koliko mililitara zauzimaju 672 grama odabrane tvari</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Primjene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Kuhanje i pečenje - pretvaranje između težine i volumena sastojaka</li>
                  <li>Laboratorijski eksperimenti - precizno mjerenje kemikalija</li>
                  <li>Farmacija - priprema lijekova i otopina</li>
                  <li>Industrija - izračun količina za proizvodnju</li>
                  <li>Svakodnevna upotreba - pretvaranje recepata iz različitih mjernih sustava</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Gustoće tvari mogu varirati ovisno o temperaturi i tlaku</li>
                  <li>Vrijednosti gustoće u kalkulatoru su približne i mogu se razlikovati od stvarnih vrijednosti</li>
                  <li>Za najpreciznije rezultate, koristite laboratorijski izmjerene gustoće</li>
                  <li>Pretvorbe su točnije za tekućine i homogene tvari</li>
                  <li>Za nehomogene tvari (poput brašna), gustoća može varirati ovisno o načinu pakiranja</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Česta Pitanja</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-1">Koliko su 672 grama vode u mililitrima?</h3>
                    <p className="text-gray-600">
                      672 grama vode jednako su 672 mililitra, jer je gustoća vode 1 g/ml.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-1">Zašto se gram i mililitar razlikuju za neke tvari?</h3>
                    <p className="text-gray-600">
                      Gram je jedinica mase, a mililitar je jedinica volumena. Njihov odnos ovisi o gustoći tvari.
                      Za vodu su jednaki (1g = 1ml), ali za druge tvari mogu biti različiti.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-1">Kako pretvoriti 672 grama brašna u mililitre?</h3>
                    <p className="text-gray-600">
                      Podijelite 672 grama s gustoćom brašna (oko 0.53 g/ml). Dakle, 672 g brašna ≈ 26.415 ml.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-1">Kako pretvoriti 672 grama ulja u mililitre?</h3>
                    <p className="text-gray-600">
                      Podijelite 672 grama s gustoćom ulja (oko 0.92 g/ml). Dakle, 672 g ulja ≈ 15.217 ml.
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </div>

          {/* Interlinking Section */}
          <section className="mt-12 bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-semibold text-gray-700 mb-6 text-center">Povezane Pretvorbe Grama u Mililitre</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {[...Array(10)].map((_, i) => {
                const gramValueBase = 15 + i;
                let gramValue = gramValueBase;
                if (gramValueBase > 1000) {
                  gramValue = gramValueBase - 1000;
                }
                // Ensure gramValue is never 0 if total_pages is a multiple and base becomes 0
                if (gramValue === 0) gramValue = 1000; 

                return (
                  <a 
                    key={gramValue}
                    href={`/gram-u-ml/${gramValue}g-u-ml`}
                    className="block p-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-center font-medium transition-colors"
                  >
                    {`${gramValue} Grama u mL`}
                  </a>
                );
              })}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

export default GramToMl672g;